import { ACTION_ARGS, Arg, MODAL_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import { MODAL_CLOSE_ANIMATION_DURATION } from '../../../utils/spds/constants/modal';
import { buildWaiter } from '@ember/test-waiters';
import { service } from '@ember/service';
import { testAwareWaitForLater, waitForRender } from '../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Super from './base.gjs';
import dialogPolyfill from 'dialog-polyfill';
import styles from './modal.module.scss';

const METADATA = Metadata.define({
  description: `
The SPDS base class for modals. It houses all common modal behavior, but does not provide a template.

### Modal or overlay?
When inheriting from the \`spds/core/modal\` class, setting the \`modal\` attribute to \`true\` will invoke the dialog element's
\`showModal\` function on opening the dialog, which automatically includes the \`::backdrop\` pseudoelement and turns off scrolling within
the \`body\` of the HTML document through the \`scrollManager\` service. If \`modal\` is instead set to \`false\` or not set, then it will
appear as an overlay above the rest of the page, which will remain interactive.

### Polyfill
Since SPDS modals rely on the dialog element, which is only recently widely supported, the \`core/modal\` class can conditionally
fall back on a [polyfill provided by the Google Chrome team](https://github.com/GoogleChrome/dialog-polyfill) in cases where users
are accessing our apps through an outdated browser.

### Hooks
The SPDS modal base class provides several hooks to add custom behavior to a modal. These include:

* \`onBeforeClose\`: this hook can be used to catch a user request to close the modal and execute custom logic to block or allow
the close process to continue. If the provided handler returns a truthy value, then closing will continue. If a falsey value is provided,
then the modal will remain open
* \`onClose\`: this hook can be used to implement custom behavior on modal close, such as user interaction tracking, or route transition
* \`onOpen\`: this hook can be used to implement custom behavior on modal open, such as user interaction tracking

### Setup
All modal setup runs in the \`setup\` hook invoked by the core/base \`setupComponent\` modifier: polyfill registration, showing the
dialog, and attaching event listeners. Subclasses that override \`setup\` should call \`super.setup()\` first to run the default modal
behavior, then add their own logic. The \`modal\` attribute on the inheriting component determines whether the dialog uses
\`showModal\` (with backdrop) or \`show\` (overlay). When \`showModal()\` runs, the instance registers with \`spds/modal\` service
\`openModalStack\` (last = topmost) and unregisters in \`willDestroy\` when the instance is torn down.
  `,
  extends: [
    pick(ACTION_ARGS, [{ name: 'onClose', required: true }, 'onBeforeClose', 'onOpen']),
    pick(MODAL_ARGS, ['closeOnClickOutside']),
  ],
  meta: {
    onShow: Arg.action({
      description: 'Hook to perform custom behavior when the modal is shown',
    }),
    renderedWithPromise: Arg.boolean({
      default: false,
      description:
        'Whether the modal is rendered with a promise; only used by global modal service for waiter handling',
    }),
    variantName: Arg.string({
      description:
        'Allows wrapping components to provide a custom component name for argument validation messages',
    }),
  },
  name: 'Modal',
});

/**
 * Base class for SPDS modal variants
 *
 * Arguments
 * @argument {boolean} closeOnClickOutside controls whether the modal should automatically close when user clicks outside of its DOM tree
 * @argument {function} onBeforeClose gives a hook where close behavior can be interrupted by custom behavior
 * @argument {function} onClose gives a hook to perform custom behavior when the modal closes
 * @argument {function} onOpen gives a hook to perform custom behavior when the modal opens
 * @argument {boolean} renderedWithPromise indicates whether the modal is being rendered with a promise, which requires special handling for waiters; only used by global modal service
 * @argument {string} variantName allows for wrapping components to provide a custom component name for argument validation messages
 */
export default class SpdsCoreModal extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseStyles = styles;
  token;

  get classesToMap() {
    return ['isVisible'];
  }

  @service bodyScrollManager;
  @service fastboot;
  @service('spds/modal') spdsModal;

  @tracked isVisible = false;

  get closeOnClickOutside() {
    return this.argOrDefault('closeOnClickOutside');
  }

  get componentName() {
    return this.args.variantName ?? this.constructor.metadata.component;
  }

  /**
   * @constructor set this.waiter to a custom waiter for this modal instance
   * using this.constructor.name and the generated uniqueIdentifier from the core/base class
   */
  constructor() {
    super(...arguments);

    this.waiter = buildWaiter(
      `@ember-simplepractice/${this.constructor.name}/${this.uniqueIdentifier}`
    );

    if (!this.args.renderedWithPromise) {
      this.token = this.waiter.beginAsync();
    }
  }

  /**
   * willDestroy lifecycle cleans up remaining modal logic,
   * allowing body scrolling to resume and removing the click handler
   * for closing on click outside if it was added
   */
  willDestroy() {
    super.willDestroy(...arguments);

    this.spdsModal.unregisterOpenModal(this.id);

    if (!this.fastboot?.isFastBoot) {
      this.bodyScrollManager.unlockScroll();
    }

    if (this.modal && this.closeOnClickOutside) {
      this.baseElement?.removeEventListener('click', this.handleClickOnDialog);
    }
  }

  /**
   * function that checks return from onBeforeClose handler to see if modal close should continue
   * defaults to allowing close when no handler is provided
   */
  async #beforeClose(evt) {
    if (this.args.onBeforeClose) {
      return Promise.resolve(this.args.onBeforeClose(evt));
    }

    return true;
  }

  /**
   * private function for cleaning up event handlers on teardown
   */
  #cleanupListeners(cancel, close) {
    this.baseElement.removeEventListener('cancel', cancel);
    this.baseElement.removeEventListener('close', close);
  }

  /**
   * private function for setting up modal-specific functionality
   * instantiates dialog element with backdrop through `showModal` and
   * prevents body scrolling through the `bodyScrollManager.lockScroll` function
   */
  #modalSetup() {
    this.baseElement.showModal();

    this.spdsModal.registerOpenModal(this.id);

    if (this.fastboot?.isFastBoot) return;

    this.bodyScrollManager.lockScroll();
  }

  /**
   * Flips `isVisible` (driving the variant's `is-visible` class via
   * `classesToMap`), waits one render so the browser commits the
   * pre-open layout at the variant's default off-screen / transparent state,
   * then calls `show`/`showModal` to add `[open]`. This two-step sequence is
   * what makes the variant SCSS exit transition (flyout slide-in,
   * full-screen slide-up, standard fade-in) actually fire — the browser uses
   * the laid-out off-screen state as the transition's "from" value when
   * `[open]` lands and the property changes to its in-position state. If
   * `show`/`showModal` runs in the same paint frame as the initial element
   * insertion, no transition fires and the dialog appears instantly.
   *
   * The `try/catch` covers the rare case where the element is disconnected
   * during the `await waitForRender()` (parent re-renders this component out
   * of the DOM mid-open). In that case `show`/`showModal` would throw on a
   * disconnected element, but the component is already being destroyed so
   * silently bailing is correct.
   */
  async #show() {
    this.isVisible = true;
    await waitForRender();

    try {
      if (this.modal) {
        this.#modalSetup();
      } else {
        this.baseElement.show();
      }
    } catch {
      // silently fail
    } finally {
      if (this.token) this.waiter.endAsync(this.token);
    }

    this.args.onShow?.();
  }

  /**
   * Shows the dialog and attaches cancel, close, and optional click-outside listeners.
   * Called from setup(); subclasses that override setup() may call this after runModalSetup() to get default open behavior.
   */
  applyModalOpen() {
    if (!this.baseElement) return;

    this.#show();

    this.args.onOpen?.();
  }

  /**
   * Ensures this.baseElement is set and registers the dialog polyfill when needed.
   * Subclasses that override setup() should call this first.
   * @param {HTMLDialogElement} [element] - Dialog element; defaults to this.baseElement when called from setup().
   */
  runModalSetup() {
    if (!this.baseElement) return;

    if (!this.fastboot?.isFastBoot && !window.HTMLDialogElement) {
      dialogPolyfill.registerDialog(this.baseElement);
      this.variantClass = 'spds-dialog-polyfill-modal';
    }

    let cancelListener = async evt => {
      evt.preventDefault();
      if (!(await this.#beforeClose(evt))) return;
      await this.close();
    };

    let closeListener = async () => {
      this.#cleanupListeners(cancelListener, closeListener);
      this.waiter.endAsync(this.token);
      this.args.onClose?.();
    };

    this.baseElement.addEventListener('cancel', cancelListener);
    this.baseElement.addEventListener('close', closeListener);

    if (this.modal && this.closeOnClickOutside) {
      this.baseElement.addEventListener('click', this.handleClickOnDialog);
    }
  }

  /**
   * Invoked by setupComponent when the dialog element is in the DOM. Runs polyfill setup, then shows
   * the dialog and attaches event listeners.
   */
  setup() {
    this.runModalSetup();
    this.applyModalOpen();
  }

  /**
   * public onBeforeClose waits for result from private beforeClose function,
   * then continues closing the modal if truthy response is received
   */
  beforeClose = async evt => {
    if (await this.#beforeClose(evt)) {
      this.close();
    }
  };

  handleClickOnDialog = async evt => {
    if (evt.target === this.baseElement) {
      await this.beforeClose();
    }
  };

  /**
   * public close function begins test waiter for modal close, removes the
   * `is-visible` class so variant SCSS can play its exit animation, waits for
   * the animation duration in non-test environments, then closes the dialog —
   * which triggers the `close` event and any consumer-provided `onClose` hook
   * (commonly used to flip a parent `{{#if}}` that destroys this component).
   * The wait keeps the element in the DOM long enough for the transition to
   * play before destruction.
   */
  close = async () => {
    this.token = this.waiter.beginAsync();
    this.isVisible = false;
    await testAwareWaitForLater(MODAL_CLOSE_ANIMATION_DURATION);
    this.baseElement?.close();
  };
}

export const CORE_MODAL_METADATA = SpdsCoreModal.metadata;
